/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';
import { BodyText, Spinner} from '@vaisala/rockhopper-components';
import { Progress } from './Progress';
import { useProject } from '../state/project';
import { DataStatus } from '../pages/DataManagementPage';
import styled from 'styled-components';
import { useAuth } from '../state/auth/AuthContext';

const ReconstructionStatusContainer = styled.div`
    height : 100%;
    width : 100%;
    display : flex;
    align-items : end;
    bottom: 0px;
`;

export interface Props {
    dataStatus : DataStatus,
    datesToReconstruct : string[]
}


export const ReconstructionStatus  : React.FC<Props> = ( props ) => { 
    const {secureFetch} = useAuth()

    const { dataStatus, datesToReconstruct } = props;

    const { project, reconstructionInfo, setReconstructionInfo } = useProject();

    const [ progressInfo, setProgressInfo ] = React.useState({
        main_label : "Reconstruction",
        main_progress : 0,
        secondary_label : "",
        secondary_progress : 0,
        current_file : ""
    });

    const progressLoop = React.useRef<ReturnType<typeof setInterval>>();

    React.useEffect(() => {
        if (reconstructionInfo.status === 'RUNNING') {
            clearInterval(progressLoop.current);
            progressLoop.current = setInterval(fetchReconstructionProgress, 1500);
        } else if(reconstructionInfo.status === 'ERROR'){
            clearInterval(progressLoop.current);
        } else if(reconstructionInfo.status === 'FINISHED'){
            clearInterval(progressLoop.current);
        } else if(reconstructionInfo.status === 'STOP'){
            clearInterval(progressLoop.current);
        }

        return () => {
            clearInterval(progressLoop.current);
        }

    }, [reconstructionInfo.status]);

    const fetchReconstructionProgress = async () => {
        try {
            const response = await secureFetch(`/api/projects/` + project.name + '/reconstruction_status');
            const data = await response.json();

            if (reconstructionInfo.status !== 'STOP'){
                const new_status: string = data.status;
                const new_secondary_label: string = data.day;
                const new_current_file : string = data.current_file;
                const new_main_progress: number = (data.total_files === 0) ? 0 : (data.total_counter / data.total_files);
                const new_secondary_pogress: number = (data.day_files === 0) ? 0 : (data.day_counter / data.day_files);

                setProgressInfo({
                    main_label: "Reconstruction ",
                    main_progress: new_main_progress,
                    secondary_label: new_secondary_label,
                    secondary_progress: new_secondary_pogress,
                    current_file : new_current_file
                });

                if (new_status === "crashed") {
                    setReconstructionInfo({...reconstructionInfo, status : "ERROR"});
                    console.log('Reconstruction process crashed');
                } else if (new_status === "finished") {
                    setReconstructionInfo({...reconstructionInfo, status : "FINISHED", files_reconstructed : data.files_generated??0});
                    console.log('Reconstruction process finished');
                }                
            }


        } catch (error) {
            console.error('Error fetching progress:', error);
        }

    }

    const renderReconstructionStatusBox = () => {
        if (dataStatus === "DATA_UPLOADED"){            
            return (
                <div>
                    <BodyText>
                    <b>{reconstructionInfo.files_reconstructed}</b> files generated.
                    </BodyText>
                    <BodyText>
                    From <b>{reconstructionInfo.dates[0].split("T")[0]}</b> to <b>{reconstructionInfo.dates[reconstructionInfo.dates.length-1].split("T")[0]}</b>.
                    </BodyText>
                </div>
            );
        }
        else if (reconstructionInfo.status === 'NONE') {
            if (dataStatus === "NO_DATA"){
                return (            
                    <div>
                    <BodyText>
                    <b>No data</b> available. 
                    </BodyText>          
                    <BodyText>
                    Please upload data to the project or wait for the upload process to finish
                    </BodyText>
                    </div>
                );
            }
            else {
                return (            
                    <div>
                    <BodyText>
                    <b>No data</b> ready to be processed.
                    </BodyText>          
                    <BodyText>
                    Please select a time period, virtual cups of interest and launch the reconstruction.
                    </BodyText>
                    </div>
                );
            }
        }
        else if (reconstructionInfo.status === 'READY') {
            return (
                <div>
                    <BodyText>
                    <b>{datesToReconstruct.length}</b> days ready to be processed.
                    </BodyText>
                    <BodyText>
                    From <b>{datesToReconstruct[0].split("T")[0]}</b> to <b>{datesToReconstruct[datesToReconstruct.length-1].split("T")[0]}</b>.
                    </BodyText>
                </div>
            );
        }
        else if (reconstructionInfo.status === 'LOADING') {
            return (
                <div>
                    <BodyText>
                    Loading reconstruction files.
                    </BodyText>
                    <BodyText>
                    From <b>{reconstructionInfo.dates[0].split("T")[0]}</b> to <b>{reconstructionInfo.dates[reconstructionInfo.dates.length-1].split("T")[0]}</b>.
                    </BodyText>
                </div>
            );
        }
        else if (reconstructionInfo.status === 'FINISHED') {
            return (
                <div>
                    <BodyText>
                    <b>{reconstructionInfo.files_reconstructed}</b> files generated.
                    </BodyText>
                    <BodyText>
                    From <b>{reconstructionInfo.dates[0].split("T")[0]}</b> to <b>{reconstructionInfo.dates[reconstructionInfo.dates.length-1].split("T")[0]}</b>.
                    </BodyText>
                </div>
            );
        }
        else if (reconstructionInfo.status === 'ERROR') {
            return (
                <div>
                    <BodyText>
                    <b>Error</b> while processing <b>{reconstructionInfo.dates.length}</b> days.
                    </BodyText>
                    <BodyText>
                    From <b>{reconstructionInfo.dates[0].split("T")[0]}</b> to <b>{reconstructionInfo.dates[reconstructionInfo.dates.length-1].split("T")[0]}</b>.
                    </BodyText>
                </div>
            );
        }
        else if (reconstructionInfo.status === 'STOP') {
            return (
                <div>
                    <Spinner />
                    <BodyText>
                        Stopping reconstruction process... This can take a few seconds... Please wait.
                    </BodyText>
                </div>
            );
        }
    }
    
    return (
        <ReconstructionStatusContainer>
        {
            reconstructionInfo.status === 'RUNNING' ?
            <Progress
            main_label={progressInfo.main_label}
            main_progress={progressInfo.main_progress}
            secondary_label={progressInfo.secondary_label}
            secondary_progress={progressInfo.secondary_progress}
            increment={progressInfo.current_file}
            /> :
            renderReconstructionStatusBox()
        }            
        </ReconstructionStatusContainer>

    );
};